<template>
  <div ref="root" class="preview" aria-hidden="true">
    <div ref="frame" class="preview__frame"></div>
  </div>
</template>

<script>
import { gsap, isTouchDevice, prefersReducedMotion } from '@/animations/gsap'

/**
 * Imagen que sigue al cursor sobre una lista. Se monta en <body> (Vue 2 no
 * tiene Teleport) para que no la afecten los transforms de la sección.
 */
export default {
  name: 'HoverPreview',
  props: {
    src: { type: String, default: '' },
    active: { type: Boolean, default: false }
  },
  data() {
    return { enabled: !isTouchDevice() && !prefersReducedMotion() }
  },
  watch: {
    src(src) {
      if (src) this.swap(src)
    },
    active(active) {
      if (!this.enabled) return
      gsap.to(this.$refs.root, active
        ? { opacity: 1, scale: 1, duration: 0.6, ease: 'expo.out' }
        : { opacity: 0, scale: 0.85, duration: 0.45, ease: 'power3.out' })
    }
  },
  mounted() {
    if (!this.enabled) return
    document.body.appendChild(this.$el)
    gsap.set(this.$refs.root, { xPercent: -50, yPercent: -50, opacity: 0, scale: 0.85 })
    this.moveX = gsap.quickTo(this.$refs.root, 'x', { duration: 0.6, ease: 'power3.out' })
    this.moveY = gsap.quickTo(this.$refs.root, 'y', { duration: 0.6, ease: 'power3.out' })
    window.addEventListener('pointermove', this.onMove)
  },
  beforeDestroy() {
    window.removeEventListener('pointermove', this.onMove)
    gsap.killTweensOf(this.$refs.root)
    if (this.$el.parentNode) this.$el.parentNode.removeChild(this.$el)
  },
  methods: {
    onMove(e) {
      if (e.pointerType !== 'mouse') return
      this.moveX(e.clientX)
      this.moveY(e.clientY)
    },
    swap(src) {
      if (!this.enabled) return
      const frame = this.$refs.frame
      const img = document.createElement('img')
      img.src = src
      img.alt = ''
      frame.appendChild(img)
      gsap.fromTo(img,
        { yPercent: 100, scale: 1.2 },
        {
          yPercent: 0,
          scale: 1,
          duration: 0.9,
          ease: 'expo.out',
          onComplete: () => {
            while (frame.firstChild && frame.firstChild !== img) frame.removeChild(frame.firstChild)
          }
        })
    }
  }
}
</script>

<style scoped>
.preview {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 40;
  width: clamp(14rem, 10rem + 10vw, 22rem);
  aspect-ratio: 4 / 5;
  pointer-events: none;
  opacity: 0;
}

.preview__frame {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: var(--c-surface);
}

.preview__frame >>> img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
